/**
 * Lazy loader for the heavy `@railgun-community/wallet` SDK (~7.5 MB minified).
 *
 * WHY: every `src/web/utils/railgun*.ts` wrapper used to `import { … } from
 * '@railgun-community/wallet'` at module top. Those wrappers are statically
 * imported by `railgunContext` (mounted app-wide, in-page on mobile-web), so the
 * whole SDK landed in the EAGER/initial bundle — even though the RAILGUN engine
 * only ever initializes on the Private-mode toggle. Routing the SDK through a
 * dynamic `import()` here moves it into an async chunk that is fetched on first
 * engine use; public-wallet users never pay for it. See
 * docs/swap-and-bridge.md / RAILGUN_HYBRID_REWRITE_PLAN.md and the bundle-size
 * notes.
 *
 * Two accessors:
 *   • `loadRailgunSdk()` — async; the normal path. Every wrapper function is
 *     already async with no eval-time SDK usage, so it does
 *     `const { foo } = await loadRailgunSdk()` at the top of its body.
 *   • `getLoadedRailgunSdk()` — sync; only for the handful of synchronous
 *     helpers (e.g. `installReorgMarginPatch`) that run *after* the engine has
 *     been initialized (which always goes through `loadRailgunSdk()` first), so
 *     the namespace is guaranteed cached by then. Throws if called before load
 *     rather than silently returning a half-initialized module.
 *
 * The dynamically-imported namespace is cached after the first call, so repeated
 * use is a plain object read with no extra network/parse cost.
 */
export type RailgunSdkModule = typeof import('@railgun-community/wallet')

let cachedSdk: RailgunSdkModule | null = null
let inFlight: Promise<RailgunSdkModule> | null = null

export async function loadRailgunSdk(): Promise<RailgunSdkModule> {
  if (cachedSdk) return cachedSdk
  // Coalesce concurrent callers onto a single import promise.
  if (!inFlight) {
    inFlight = import('@railgun-community/wallet').then((mod) => {
      cachedSdk = mod
      inFlight = null
      return mod
    })
  }
  return inFlight
}

export function getLoadedRailgunSdk(): RailgunSdkModule {
  if (!cachedSdk) {
    throw new Error(
      'Railgun SDK accessed before it was loaded — call loadRailgunSdk() (or initialize the engine) first.'
    )
  }
  return cachedSdk
}
